import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Title1, Subtitle2, Spinner, Button, Tooltip, tokens } from '@fluentui/react-components'
import { ArrowLeft20Regular } from '@fluentui/react-icons'
import { apiFetchJson } from '../lib/apiFetch'
import { ATTRIBUTION_COLOR } from '../lib/attributionColors'
import { buildRows, isAttributedElsewhere } from '../lib/personRows'
import { SERVICE_LABELS } from '../lib/services'
import Lozenge from '../components/Lozenge'
import type { GitHubResponse, GitHubUserRow, LicensesResponse, LicenseTypeConfig, LicenseTypesResponse, OrphanCheckResult } from '../types'

const PERMISSION_COLOR: Record<GitHubUserRow['access'][number]['permission'], 'subtle' | 'informative' | 'warning' | 'severe' | 'danger'> = {
  pull: 'subtle',
  triage: 'informative',
  push: 'warning',
  maintain: 'severe',
  admin: 'danger',
}

/** One GitHub account's teams + resolved repo access, inline — same content the Users tab's
 * AccessDetailDialog shows on /users/github, just embedded in this page instead of a dialog
 * (this page already IS the "full detail" surface, so no popup needed here). */
function GitHubAccountDetail({ user }: { user: GitHubUserRow }) {
  return (
    <div style={{ marginTop: 8 }}>
      {user.access.length === 0 ? (
        <p style={{ fontSize: 13, color: tokens.colorNeutralForeground3 }}>No repository access.</p>
      ) : (
        <ul style={{ marginTop: 4 }}>
          {user.access.map(a => (
            <li key={a.repoName} style={{ marginBottom: 4 }}>
              <strong>{a.repoName}</strong> <Lozenge color="subtle">{a.visibility}</Lozenge>{' '}
              <Lozenge color={PERMISSION_COLOR[a.permission]}>{a.permission}</Lozenge>
              <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>
                via {a.via.map(v => (v.type === 'direct' ? 'direct grant' : `team "${v.teamName}"`)).join(', ')}
              </div>
            </li>
          ))}
        </ul>
      )}
      {user.teams.length > 0 && (
        <div style={{ fontSize: 13, marginTop: 8 }}>Teams: {user.teams.map(t => t.name).join(', ')}</div>
      )}
    </div>
  )
}

function statusSentence(entraStatus: OrphanCheckResult['entraStatus']): string {
  if (entraStatus === 'enabled') return 'This account is active in Entra ID.'
  if (entraStatus === 'disabled') return 'This account is blocked/disabled in Entra ID.'
  return 'This email has no matching account in Entra ID at all (fully removed or never existed).'
}

interface NotePart {
  text: string
  /** Overrides the default gray — reserved for "which raw account is this actually" references
   * (Linked/Shared/External account), colored per override kind (see ATTRIBUTION_COLOR) so they
   * read as the same kind of annotation as the matching badge elsewhere in the app. */
  color?: string
}

/** Note lines for a license line whose resolution carries extra context worth surfacing —
 * external/shared/linked account references, a comment, who's Responsible, and a "responsible
 * person blocked" warning. The Responsible line is skipped only when `rowPersonEmail` (the page
 * we're currently viewing) IS that responsible person — showing "Responsible: Anatolii" on
 * Anatolii's own page is redundant, but omitting it everywhere left the shared/external
 * account's own page with no way to see who's accountable for it at all. */
function assignmentNoteParts(a: OrphanCheckResult, rowPersonEmail: string): NotePart[] {
  const parts: NotePart[] = []
  if (a.resolutionKind === 'external') parts.push({ text: `External account: ${a.rawEmail}`, color: ATTRIBUTION_COLOR.external })
  if (a.resolutionKind === 'shared') parts.push({ text: `Shared account: ${a.rawEmail}`, color: ATTRIBUTION_COLOR.shared })
  if (a.resolutionKind === 'manual') parts.push({ text: `Linked account: ${a.rawEmail}`, color: ATTRIBUTION_COLOR.manual })
  if (a.accountStatus && a.accountStatus !== 'active') parts.push({ text: `⚠ blocked in service (${a.accountStatus}) — wasted seat` })
  if (a.comment) parts.push({ text: a.comment })
  if (a.responsibleDisplayName && a.responsibleEmail?.toLowerCase() !== rowPersonEmail.toLowerCase()) {
    parts.push({ text: `Responsible: ${a.responsibleDisplayName}` })
  }
  if (a.responsibleNeedsReview) parts.push({ text: '⚠ responsible person is blocked/gone' })
  return parts
}

/** Full page for one person's licenses across every service — a Dialog used to hold this, but a
 * person with many services/licenses (or the richer per-assignment notes added since) doesn't
 * fit in a modal; a page scrolls naturally and can grow without redesigning a popup each time. */
export default function PersonDetail() {
  const { email = '' } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<LicensesResponse | null>(null)
  const [configByService, setConfigByService] = useState<Record<string, Record<string, LicenseTypeConfig>>>({})
  const [githubUsers, setGithubUsers] = useState<GitHubUserRow[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    apiFetchJson<LicensesResponse>('/api/licenses')
      .then(setData)
      .catch(e => setError(String(e)))
  }, [])

  useEffect(() => {
    apiFetchJson<GitHubResponse>('/api/github')
      .then(res => setGithubUsers(res.users))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!data) return
    const serviceIds = [...new Set(data.results.map(r => r.serviceId))]
    Promise.all(
      serviceIds.map(id =>
        apiFetchJson<LicenseTypesResponse>(`/api/license-types?serviceId=${id}`).then(res => [id, res] as const),
      ),
    )
      .then(entries => {
        const map: Record<string, Record<string, LicenseTypeConfig>> = {}
        for (const [id, res] of entries) {
          map[id] = Object.fromEntries(res.types.map(t => [t.licenseType, t]))
        }
        setConfigByService(map)
      })
      .catch(e => setError(String(e)))
  }, [data])

  const row = useMemo(() => {
    if (!data) return null
    const rows = buildRows(data.persons, data.results)
    return rows.find(r => r.personEmail.toLowerCase() === email.toLowerCase()) ?? null
  }, [data, email])

  // GitHub isn't a licensed service (no ServiceAssignment rows), so it doesn't appear in
  // row.byService above — matched separately here by this person's email against GitHubUserLink
  // instead (see docs/ARCHITECTURE.md's "GitHub access sync").
  const ownGithubAccount = row ? githubUsers.find(u => u.link?.status === 'linked' && u.link.personEmail === row.personEmail) : undefined
  const responsibleForGithub = row ? githubUsers.filter(u => u.link?.responsibleEmail === row.personEmail) : []

  if (error) return <p>Error: {error}</p>
  if (!data) return <Spinner label="Loading..." />

  return (
    <div>
      <Button appearance="subtle" icon={<ArrowLeft20Regular />} onClick={() => navigate('/')} style={{ marginBottom: 12 }}>
        Back to Licenses
      </Button>

      {!row ? (
        <p>No person found for "{email}".</p>
      ) : (
        <>
          <Title1>{row.personDisplayName}</Title1>
          <Subtitle2 style={{ display: 'block', marginTop: 4, marginBottom: 24 }}>
            {row.personEmail}
            {row.department && ` · ${row.department}`}
          </Subtitle2>

          <p>{statusSentence(row.entraStatus)}</p>

          <h3 style={{ marginBottom: 8 }}>Licenses</h3>
          {Object.keys(row.byService).length === 0 ? (
            <p>This person currently holds no licenses on any connected service.</p>
          ) : (
            Object.entries(row.byService).map(([serviceId, assignments]) => {
              // source/lastSyncedAt come from one sync batch per service, so every assignment
              // here shares the same values — state them once, not per line.
              const [{ source, lastSyncedAt }] = assignments
              // This person's own licenses first, licenses attributed here from another raw
              // account after (manually linked, or a shared/external account this person is
              // Responsible for) — otherwise they interleave in whatever order the sync happened
              // to report them, and an attributed account can even repeat a license type the
              // person already has (e.g. a test account mirroring their real one), reading as a
              // confusing jumble.
              const sortedAssignments = [...assignments].sort(
                (a, b) =>
                  Number(isAttributedElsewhere(a, row.personEmail)) - Number(isAttributedElsewhere(b, row.personEmail)),
              )
              return (
                <div key={serviceId} style={{ marginBottom: 16 }}>
                  <strong>{SERVICE_LABELS[serviceId] ?? serviceId}</strong> — synced {lastSyncedAt}, source: {source}
                  <ul style={{ marginTop: 4 }}>
                    {sortedAssignments.map(a => {
                      const displayName = configByService[serviceId]?.[a.licenseType]?.displayName || a.licenseType
                      const noteParts = assignmentNoteParts(a, row.personEmail)
                      // A genuine automatic Entra match, just reported by the service under an
                      // alias (proxyAddress) rather than the person's primary email — not an
                      // override, purely informational, so a small superscript instead of a full
                      // note line.
                      const viaAlias = a.resolutionKind === 'entra' && a.rawEmail !== row.personEmail
                      return (
                        <li key={`${a.rawEmail}-${a.licenseType}`} style={{ marginBottom: 4 }}>
                          <strong>{displayName}</strong>
                          {viaAlias && (
                            <Tooltip
                              content={`Genuine license for this person — the service reported it under an alias address (${a.rawEmail}) rather than their primary email.`}
                              relationship="description"
                              withArrow
                            >
                              <sup style={{ cursor: 'help', color: tokens.colorNeutralForeground3, marginLeft: 2 }}>*</sup>
                            </Tooltip>
                          )}
                          {a.costPerSeat ? ` ($${a.costPerSeat}/seat)` : ''}
                          {noteParts.map((part, i) => (
                            <div key={i} style={{ fontSize: 12, color: part.color ?? tokens.colorNeutralForeground3 }}>
                              {part.text}
                            </div>
                          ))}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })
          )}

          {(ownGithubAccount || responsibleForGithub.length > 0) && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ marginBottom: 8 }}>GitHub</h3>
              {ownGithubAccount && (
                <div style={{ marginBottom: 12 }}>
                  <strong>@{ownGithubAccount.login}</strong>{' '}
                  {ownGithubAccount.role === 'admin' && <Lozenge color="danger">Org admin</Lozenge>}
                  <GitHubAccountDetail user={ownGithubAccount} />
                </div>
              )}
              {responsibleForGithub.map(u => (
                <div key={u.login} style={{ marginBottom: 12 }}>
                  <strong>@{u.login}</strong>{' '}
                  <Lozenge color={u.link?.status === 'shared' ? 'warning' : 'informative'}>
                    Responsible for {u.link?.status} account
                  </Lozenge>
                  <GitHubAccountDetail user={u} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
